export type IsoDate = string;

export type SelectableOutcome = "T" | "W" | "C" | "B";
export type ImportedOutcome = "M";
export type Outcome = SelectableOutcome | ImportedOutcome;

export const SELECTABLE_OUTCOMES: readonly SelectableOutcome[] = ["T", "W", "C", "B"];
export const IMPORT_ONLY_OUTCOMES: readonly ImportedOutcome[] = ["M"];

export type RollingPeriod = Readonly<{ kind: "rolling"; days: number }>;
export type CalendarPeriod = Readonly<{ kind: "week" | "month" }>;
export type RepeatPeriod = RollingPeriod | CalendarPeriod;

export type WholePeriod = Readonly<{ kind: "whole" }>;
export type PartialPeriod = Readonly<{ kind: "first" | "last"; days: number }>;
export type RepeatPart = WholePeriod | PartialPeriod;

export interface RepeatRule {
  readonly quota: number;
  /** Sunday=0 through Saturday=6. null means any day. */
  readonly weekdays: readonly number[] | null;
  readonly period: RepeatPeriod;
  readonly part: RepeatPart;
}

export interface EffectiveRuleVersion {
  readonly id: string;
  readonly effectiveFrom: IsoDate;
  readonly effectiveToExclusive?: IsoDate | null;
  readonly rule: RepeatRule;
}

export interface ActivePeriod {
  readonly from: IsoDate;
  readonly toExclusive: IsoDate | null;
}

export interface Category {
  readonly id: string;
  readonly sourceKey?: string;
  readonly name: string;
  readonly activePeriods: readonly ActivePeriod[];
  readonly operationOrder: readonly string[];
}

export interface Operation {
  readonly id: string;
  readonly categoryId: string;
  readonly name: string;
  readonly private: boolean;
  readonly activePeriods: readonly ActivePeriod[];
  readonly ruleVersions: readonly EffectiveRuleVersion[];
  readonly sourceKey?: string;
}

export interface Entry {
  readonly operationId: string;
  readonly date: IsoDate;
  readonly outcome: Outcome;
}

export interface ScoreBand {
  readonly id: string;
  readonly label: string;
  readonly min: number;
}

export interface TrailDefinition {
  readonly id: string;
  readonly label: string;
  readonly days: number;
}

export interface MatrixDocument {
  readonly ownerId: string;
  readonly categories: readonly Category[];
  readonly operations: readonly Operation[];
  readonly entries: readonly Entry[];
  readonly bands?: readonly ScoreBand[];
  readonly target?: number;
}

export const DEFAULT_DAILY_RULE: RepeatRule = Object.freeze({
  quota: 1,
  weekdays: null,
  period: Object.freeze({ kind: "rolling", days: 1 }),
  part: Object.freeze({ kind: "whole" }),
});

export const DEFAULT_BANDS: readonly ScoreBand[] = Object.freeze([
  Object.freeze({ id: "off", label: "Off track", min: 0 }),
  Object.freeze({ id: "building", label: "Building", min: 50 }),
  Object.freeze({ id: "close", label: "Close", min: 70 }),
  Object.freeze({ id: "on-target", label: "On target", min: 85 }),
]);

export function isSelectableOutcome(value: unknown): value is SelectableOutcome {
  return typeof value === "string" && SELECTABLE_OUTCOMES.includes(value as SelectableOutcome);
}

export function isImportOutcome(value: unknown): value is ImportedOutcome {
  return value === "M";
}

export function assertSelectableOutcome(value: unknown): asserts value is SelectableOutcome {
  if (!isSelectableOutcome(value)) {
    throw new DomainError("OUTCOME_NOT_SELECTABLE", `${String(value)} is not a selectable outcome`);
  }
}

export function assertImportedOutcome(value: unknown): asserts value is Outcome {
  if (!isSelectableOutcome(value) && !isImportOutcome(value)) {
    throw new DomainError("INVALID_IMPORTED_OUTCOME", `${String(value)} is not a supported imported outcome`);
  }
}

export class DomainError extends Error {
  public readonly code: string;

  public constructor(code: string, message: string) {
    super(message);
    this.name = "DomainError";
    this.code = code;
  }
}
